import { Component, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterModule, Router } from '@angular/router';
import { FormsModule } from '@angular/forms';
import { HttpClient } from '@angular/common/http';
import { environment } from '../../../environments/environment';

/** Shape of a single page row from GET /api/v1/urls/content-inventory */
interface InventoryPage {
  url: string;
  analysisId: string;
  analyzedAt: string;
  onpageScore: number;
  wordCount: number;
  statusCode: number;
  titleLength: number;
  descriptionLength: number;
  readabilityIndex: number;
  h1Count: number;
  h2Count: number;
  h3Count: number;
  internalLinksCount: number;
  externalLinksCount: number;
  criticalIssuesCount: number;
  warningsCount: number;
  topicSignalsCount: number;
  rankedKeywordsCount: number;
}

interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

type SortField = 'analyzedAt' | 'onpageScore' | 'wordCount' | 'statusCode' | 'url';

@Component({
  selector: 'app-content-inventory',
  standalone: true,
  imports: [CommonModule, RouterModule, FormsModule],
  templateUrl: './content-inventory.html',
  styleUrl: './content-inventory.css'
})
export class ContentInventoryComponent implements OnInit {
  private readonly API_URL = environment.apiUrl + '/v1';

  loading = true;
  error: string | null = null;
  pages: InventoryPage[] = [];
  pagination: Pagination = { page: 1, limit: 50, total: 0, totalPages: 0 };

  // Search & Sort
  searchQuery = '';
  sortBy: SortField = 'analyzedAt';
  sortOrder: 'asc' | 'desc' = 'desc';

  private searchTimeout: any = null;

  constructor(private http: HttpClient, private router: Router) {}

  ngOnInit() {
    this.loadInventory();
  }

  loadInventory() {
    this.loading = true;
    this.error = null;

    const params = new URLSearchParams();
    params.set('page', String(this.pagination.page));
    params.set('limit', String(this.pagination.limit));
    params.set('sortBy', this.sortBy);
    params.set('sortOrder', this.sortOrder);
    if (this.searchQuery.trim()) {
      params.set('search', this.searchQuery.trim());
    }

    this.http.get<{ success: boolean; data: { pages: InventoryPage[]; pagination: Pagination } }>(
      `${this.API_URL}/urls/content-inventory?${params.toString()}`
    ).subscribe({
      next: (res) => {
        this.pages = res.data.pages;
        this.pagination = res.data.pagination;
        this.loading = false;
      },
      error: (err) => {
        console.error('Failed to load content inventory:', err);
        this.error = 'Could not load content inventory. Is the backend running?';
        this.loading = false;
      }
    });
  }

  /** Navigate to the url-detail page */
  goToUrlDetail(url: string) {
    this.router.navigate(['/inventory', encodeURIComponent(url)]);
  }

  /** Debounced search — waits 300ms after user stops typing */
  onSearchChange() {
    clearTimeout(this.searchTimeout);
    this.searchTimeout = setTimeout(() => {
      this.pagination.page = 1;
      this.loadInventory();
    }, 300);
  }

  /** Toggle sort: if same field → flip order, else → set field desc */
  toggleSort(field: SortField) {
    if (this.sortBy === field) {
      this.sortOrder = this.sortOrder === 'desc' ? 'asc' : 'desc';
    } else {
      this.sortBy = field;
      this.sortOrder = 'desc';
    }
    this.pagination.page = 1;
    this.loadInventory();
  }

  /** Sort indicator arrow */
  sortIcon(field: SortField): string {
    if (this.sortBy !== field) return '';
    return this.sortOrder === 'asc' ? ' ↑' : ' ↓';
  }

  /** Go to a specific page */
  goToPage(page: number) {
    if (page < 1 || page > this.pagination.totalPages) return;
    this.pagination.page = page;
    this.loadInventory();
  }

  /** Format URL for display (truncate long ones) */
  formatUrl(url: string): string {
    try {
      const u = new URL(url);
      const path = u.pathname + u.search;
      const display = u.hostname + (path.length > 1 ? path : '');
      return display.length > 70 ? display.slice(0, 67) + '…' : display;
    } catch {
      return url.length > 70 ? url.slice(0, 67) + '…' : url;
    }
  }

  /** Relative time label */
  timeAgo(dateStr: string): string {
    const now = Date.now();
    const then = new Date(dateStr).getTime();
    const diffMs = now - then;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return 'just now';
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffHr = Math.floor(diffMin / 60);
    if (diffHr < 24) return `${diffHr}h ago`;
    const diffDay = Math.floor(diffHr / 24);
    if (diffDay < 30) return `${diffDay}d ago`;
    return new Date(dateStr).toLocaleDateString();
  }

  /** Score color class */
  scoreClass(score: number): string {
    if (score >= 80) return 'score-high';
    if (score >= 50) return 'score-mid';
    return 'score-low';
  }

  /** Status code class */
  statusCodeClass(code: number): string {
    if (code >= 200 && code < 300) return 'status-ok';
    if (code >= 300 && code < 400) return 'status-redirect';
    return 'status-error';
  }

  /** Issues badge class */
  issuesClass(critical: number, warnings: number): string {
    if (critical > 0) return 'issues-critical';
    if (warnings > 0) return 'issues-warning';
    return 'issues-clean';
  }

  /** Whether the inventory is empty (no pages at all, not just filtered) */
  get isEmpty(): boolean {
    return !this.loading && !this.error && this.pages.length === 0 && !this.searchQuery.trim();
  }

  /** Whether a search returned no results */
  get isEmptySearch(): boolean {
    return !this.loading && !this.error && this.pages.length === 0 && !!this.searchQuery.trim();
  }
}
